<template>
  <div class="shipping-page">
    <!-- Hero Section -->
    <section class="page-hero">
      <div class="hero-overlay"></div>
      <div class="container">
        <div class="hero-content" data-aos="fade-up">
          <h1 class="page-title">Shipping & Returns</h1>
          <p class="page-subtitle">Everything you need to know about our shipping policies and hassle-free returns.</p>
        </div>
      </div>
    </section>

    <!-- Quick Info Cards -->
    <section class="quick-info">
      <div class="container">
        <div class="row g-4">
          <div class="col-md-3" data-aos="fade-up" data-aos-delay="0">
            <div class="info-card">
              <i class="fas fa-shipping-fast"></i>
              <h4>Free Shipping</h4>
              <p>On orders over ₹999</p>
            </div>
          </div>
          
          <div class="col-md-3" data-aos="fade-up" data-aos-delay="100">
            <div class="info-card">
              <i class="fas fa-clock"></i>
              <h4>Fast Delivery</h4>
              <p>3-7 business days</p>
            </div>
          </div>
          
          <div class="col-md-3" data-aos="fade-up" data-aos-delay="200">
            <div class="info-card">
              <i class="fas fa-undo-alt"></i>
              <h4>Easy Returns</h4>
              <p>15-day return policy</p>
            </div>
          </div>
          
          <div class="col-md-3" data-aos="fade-up" data-aos-delay="300">
            <div class="info-card">
              <i class="fas fa-shield-alt"></i>
              <h4>Secure Packaging</h4>
              <p>Safe & protected</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Shipping Information -->
    <section class="shipping-info text-start">
      <div class="container">
        <div class="row g-5">
          <div class="col-lg-6" data-aos="fade-right">
            <div class="section-header text-start">
              <span class="section-subtitle">Delivery Information</span>
              <h2 class="section-title-modern">Shipping Policy</h2>
              <div class="title-underline ms-0"></div>
            </div>
            
            <div class="policy-content">
              <div class="policy-item">
                <div class="policy-icon">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="policy-text">
                  <h4>Domestic Shipping</h4>
                  <p>We deliver across India within 3-7 business days. Free shipping on all orders above ₹999. Standard shipping charges of ₹99 apply for orders below ₹999.</p>
                </div>
              </div>
              
              <div class="policy-item">
                <div class="policy-icon">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="policy-text">
                  <h4>Processing Time</h4>
                  <p>All orders are processed within 1-2 business days. Orders placed on weekends or holidays will be processed the next business day.</p>
                </div>
              </div>
              
              <div class="policy-item">
                <div class="policy-icon">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="policy-text">
                  <h4>Order Tracking</h4>
                  <p>Once your order ships, you'll receive a tracking number via email to monitor your package in real-time.</p>
                </div>
              </div>
              
              <div class="policy-item">
                <div class="policy-icon">
                  <i class="fas fa-check-circle"></i>
                </div>
                <div class="policy-text">
                  <h4>Custom Orders</h4>
                  <p>Personalized items may take 5-10 additional business days for crafting before shipping.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-lg-6" data-aos="fade-left">
            <div class="shipping-image-wrapper">
              <img src="https://images.unsplash.com/photo-1607083206869-4c7672e72a8a?w=800&h=600&fit=crop" alt="Shipping" class="shipping-image">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Shipping Rates Table -->
    <section class="shipping-rates">
      <div class="container">
        <div class="section-header">
          <span class="section-subtitle">Pricing</span>
          <h2 class="section-title-modern">Shipping Rates</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="table-wrapper" data-aos="fade-up">
          <table class="rates-table">
            <thead>
              <tr>
                <th>Order Value</th>
                <th>Shipping Charge</th>
                <th>Delivery Time</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Below ₹999</td>
                <td>₹99</td>
                <td>3-7 business days</td>
              </tr>
              <tr class="featured-row">
                <td>₹999 and above</td>
                <td><strong>FREE</strong></td>
                <td>3-7 business days</td>
              </tr>
              <tr>
                <td>Express Shipping</td>
                <td>₹199</td>
                <td>2-3 business days</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Returns Policy -->
    <section id="returns" class="returns-info">
      <div class="container">
        <div class="row g-5">
          <div class="col-lg-6" data-aos="fade-right">
            <div class="returns-image-wrapper">
              <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop" alt="Returns" class="returns-image">
            </div>
          </div>
          
          <div class="col-lg-6 text-start" data-aos="fade-left">
            <div class="section-header text-start">
              <span class="section-subtitle">Hassle-Free Returns</span>
              <h2 class="section-title-modern">Return & Exchange Policy</h2>
              <div class="title-underline ms-0"></div>
            </div>
            
            <div class="policy-content">
              <div class="policy-item">
                <div class="policy-icon">
                  <i class="fas fa-calendar-check"></i>
                </div>
                <div class="policy-text">
                  <h4>15-Day Return Window</h4>
                  <p>You have 15 days from the date of delivery to initiate a return. Items must be unused, in original condition with all tags attached.</p>
                </div>
              </div>
              
              <div class="policy-item">
                <div class="policy-icon">
                  <i class="fas fa-exchange-alt"></i>
                </div>
                <div class="policy-text">
                  <h4>Exchange Available</h4>
                  <p>We offer exchanges for different sizes, colors, or similar products. Contact us within the return window to arrange an exchange.</p>
                </div>
              </div>
              
              <div class="policy-item">
                <div class="policy-icon">
                  <i class="fas fa-money-bill-wave"></i>
                </div>
                <div class="policy-text">
                  <h4>Refund Process</h4>
                  <p>Refunds are processed within 7-10 business days after we receive and inspect the returned item. Amount will be credited to original payment method.</p>
                </div>
              </div>
              
              <div class="policy-item">
                <div class="policy-icon">
                  <i class="fas fa-ban"></i>
                </div>
                <div class="policy-text">
                  <h4>Non-Returnable Items</h4>
                  <p>Personalized/custom items, sale items (unless defective), and gift cards are non-returnable.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="faq-section">
      <div class="container">
        <div class="section-header">
          <span class="section-subtitle">Common Questions</span>
          <h2 class="section-title-modern">Shipping & Returns FAQ</h2>
          <div class="title-underline"></div>
        </div>
        
        <div class="row g-4">
          <div class="col-md-6" data-aos="fade-up" data-aos-delay="0">
            <div class="faq-card">
              <h4 class="faq-question">What if my item arrives damaged?</h4>
              <p class="faq-answer">Contact us immediately with photos. We'll arrange a replacement or full refund at no cost to you.</p>
            </div>
          </div>
          
          <div class="col-md-6" data-aos="fade-up" data-aos-delay="100">
            <div class="faq-card">
              <h4 class="faq-question">Do I have to pay for return shipping?</h4>
              <p class="faq-answer">Return shipping costs are the customer's responsibility unless the item is defective or we made an error.</p>
            </div>
          </div>
          
          <div class="col-md-6" data-aos="fade-up" data-aos-delay="200">
            <div class="faq-card">
              <h4 class="faq-question">Can I cancel my order?</h4>
              <p class="faq-answer">Yes, if your order hasn't shipped yet. Contact us within 24 hours of ordering for cancellation.</p>
            </div>
          </div>
          
          <div class="col-md-6" data-aos="fade-up" data-aos-delay="300">
            <div class="faq-card">
              <h4 class="faq-question">Do you ship internationally?</h4>
              <p class="faq-answer">Currently, we only ship within India. International shipping will be available soon!</p>
            </div>
          </div>
        </div>
        
        <div class="text-center mt-5" data-aos="fade-up">
          <p class="more-questions">Still have questions?</p>
          <a href="/contact" class="btn-contact">
            Contact Us
            <i class="fas fa-arrow-right"></i>
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ShippingReturnsPage',
  mounted() {
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-in-out',
        once: true,
        offset: 100
      });
    }
    
    // Scroll to returns section if hash is present
    if (window.location.hash === '#returns') {
      setTimeout(() => {
        document.getElementById('returns')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700&display=swap');

.shipping-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ========== HERO SECTION ========== */
.page-hero {
  height: 400px;
  background: linear-gradient(135deg, rgba(118, 172, 175, 0.9), rgba(98, 152, 155, 0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: 80px;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
}

.hero-content {
  text-align: center;
  color: white;
  position: relative;
  z-index: 2;
}

.page-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.page-subtitle {
  font-size: clamp(1rem, 2vw, 1.3rem);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
}

/* ========== QUICK INFO CARDS ========== */
.quick-info {
  padding: 80px 0;
  background: white;
  margin-top: -60px;
  position: relative;
  z-index: 10;
}

.info-card {
  background: white;
  padding: 35px 25px;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
  transition: all 0.3s;
}

.info-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0,0,0,0.15);
}

.info-card i {
  font-size: 2.5rem;
  color: rgb(118, 172, 175);
  margin-bottom: 15px;
}

.info-card h4 {
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 5px;
}

.info-card p {
  color: #718096;
  margin: 0;
}

/* ========== SHIPPING INFO SECTION ========== */
.shipping-info {
  padding: 100px 0;
  background: #f8fafb;
}

.section-header {
  margin-bottom: 40px;
}

.section-subtitle {
  color: rgb(118, 172, 175);
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: block;
  margin-bottom: 10px;
}

.section-title-modern {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 20px;
}

.title-underline {
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, rgb(118, 172, 175), rgb(98, 152, 155));
  margin: 0 auto;
  border-radius: 2px;
}

.policy-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.policy-item {
  display: flex;
  gap: 20px;
}

.policy-icon {
  width: 50px;
  height: 50px;
  background: rgba(118, 172, 175, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.policy-icon i {
  color: rgb(118, 172, 175);
  font-size: 1.3rem;
}

.policy-text h4 {
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 8px;
}

.policy-text p {
  color: #718096;
  line-height: 1.7;
  margin: 0;
}

.shipping-image-wrapper,
.returns-image-wrapper {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 50px rgba(0,0,0,0.15);
}

.shipping-image,
.returns-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* ========== SHIPPING RATES TABLE ========== */
.shipping-rates {
  padding: 100px 0;
  background: white;
}

.table-wrapper {
  max-width: 900px;
  margin: 0 auto;
  overflow-x: auto;
  border-radius: 15px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
}

.rates-table {
  width: 100%;
  background: white;
  border-collapse: collapse;
}

.rates-table thead {
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
}

.rates-table th {
  padding: 20px;
  text-align: left;
  font-weight: 600;
  font-size: 1.05rem;
}

.rates-table td {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  color: #4a5568;
}

.rates-table tbody tr:hover {
  background: #f8fafb;
}

.featured-row {
  background: rgba(118, 172, 175, 0.05);
}

.featured-row td {
  font-weight: 600;
  color: #2d3748;
}

/* ========== RETURNS INFO SECTION ========== */
.returns-info {
  padding: 100px 0;
  background: #f8fafb;
}

/* ========== RETURN STEPS SECTION ========== */
.return-steps {
  padding: 100px 0;
  background: white;
}

.steps-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.step-item {
  flex: 1;
  min-width: 200px;
  text-align: center;
}

.step-number {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.3rem;
  margin: 0 auto 15px;
}

.step-icon {
  font-size: 2.5rem;
  color: rgb(118, 172, 175);
  margin-bottom: 15px;
}

.step-title {
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 10px;
}

.step-description {
  color: #718096;
  line-height: 1.6;
  font-size: 0.95rem;
}

.step-arrow {
  color: rgb(118, 172, 175);
  font-size: 1.5rem;
}

/* ========== FAQ SECTION ========== */
.faq-section {
  padding: 100px 0;
  background: #f8fafb;
}

.faq-card {
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  height: 100%;
  transition: all 0.3s;
}

.faq-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
}

.faq-question {
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 12px;
  font-size: 1.1rem;
}

.faq-answer {
  color: #718096;
  line-height: 1.7;
  margin: 0;
}

.more-questions {
  font-size: 1.2rem;
  color: #4a5568;
  margin-bottom: 20px;
}

.btn-contact {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 35px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.05rem;
  transition: all 0.3s;
}

.btn-contact:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(118, 172, 175, 0.4);
  color: white;
}

/* ========== MOBILE RESPONSIVE ========== */
@media (max-width: 768px) {
  .page-hero {
    height: 300px;
    margin-top: 70px;
  }
  
  .quick-info {
    padding: 60px 0;
    margin-top: -40px;
  }
  
  .shipping-info,
  .shipping-rates,
  .returns-info,
  .return-steps,
  .faq-section {
    padding: 60px 0;
  }
  
  .steps-wrapper {
    flex-direction: column;
  }
  
  .step-arrow {
    transform: rotate(90deg);
  }
  
  .rates-table th,
  .rates-table td {
    padding: 12px;
    font-size: 0.9rem;
  }
}
</style>